/**
 * 동기화 대기열 항목 라벨 매핑
 * SyncPage에서 사람이 읽을 수 있는 설명을 표시하기 위한 상수 및 헬퍼 함수
 */

import type { EntityType, SyncOperation } from "@/types";
import type { SyncPayload } from "@/lib/db/types";
import { MeasureType, MeasureTypeNames, MACHINE_TITLES, type MachineKey } from "@ict/shared";

/**
 * GraphQL 키("DC_VOLTAGE") 또는 camelCase("dcVoltage")를 한글명으로 변환
 */
function toMeasureLabel(key: string): string {
  // camelCase 키로 직접 조회
  if (key in MeasureTypeNames) {
    return MeasureTypeNames[key as keyof typeof MeasureTypeNames];
  }
  // UPPER_CASE GraphQL 키 → MeasureType enum에서 camelCase 값으로 변환
  const camelCase = MeasureType[key as keyof typeof MeasureType];
  if (camelCase && camelCase in MeasureTypeNames) {
    return MeasureTypeNames[camelCase as keyof typeof MeasureTypeNames];
  }
  return key;
}

/**
 * 엔티티 타입 → 한글 라벨
 */
export const TYPE_LABELS: Record<EntityType, string> = {
  checklist: "점검항목",
  equipment: "설비",
  machine: "설비정보",
  data: "데이터",
  photo: "사진",
  project: "프로젝트",
  measure: "계측",
};

/**
 * 작업 유형 → 한글 라벨
 */
export const OPERATION_LABELS: Record<SyncOperation, string> = {
  create: "추가",
  update: "수정",
  delete: "삭제",
};

/**
 * 패치 필드명 → 한글 라벨
 * CheckListItem, DataItem, MachineInfo 필드를 한글로 변환
 */
export const FIELD_LABELS: Record<string, string> = {
  // CheckListItem 필드
  result: "점검결과",
  note: "비고",
  underReason: "부적합 원인",
  underSolution: "조치 내용",
  image: "사진",
  imageO: "적합 사진",
  imageX: "부적합 사진",
  // DataItem 필드
  value: "측정값",
  // Measure 필드
  ratedVoltage: "정격전압",
  measuredVoltage: "측정전압",
  measuredValue: "측정값",
  standardValue: "기준값",
  discussion: "기술소견",
  remark: "비고",
  // MachineInfo 필드
  title: "설비명",
  position: "설치위치",
  modelNo: "모델번호",
  modelYear: "제조연도",
  modelCreator: "제조사",
  checkName: "점검자",
  checkStartDate: "점검시작일",
  checkEndDate: "점검종료일",
};

/** 필드명을 한글 라벨로 변환 (매핑 없으면 원본 반환) */
export function toFieldLabel(field: string): string {
  return FIELD_LABELS[field] || field;
}

/**
 * 문서 키 → 문서 라벨
 * docKey 형식: "doc-{N}" → "문서 {N}"
 */
export function getDocKeyLabel(docKey: string | undefined): string {
  if (!docKey) return "";

  // doc-1 → 문서 1, doc-2 → 문서 2 패턴
  const match = docKey.match(/^doc-(\d+)$/);
  if (match) {
    return `문서 ${match[1]}`;
  }

  // 패턴이 맞지 않으면 원본 반환
  return docKey;
}

/**
 * 값을 표시용 문자열로 변환 (null/undefined → "(없음)")
 */
export function formatValue(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "(없음)";
  }
  // 긴 텍스트는 말줄임표 처리 (최대 20자)
  if (value.length > 20) {
    return value.slice(0, 20) + "...";
  }
  return value;
}

/**
 * 단일 패치를 상세 문자열로 변환
 * 예: "누수 확인 여부처리 - 점검결과: 적합 → 부적합"
 */
export function formatPatchDetail(patch: {
  field: string;
  value?: string | null;
  oldValue?: string | null;
  itemTitle?: string;
}): string {
  const fieldLabel = toFieldLabel(patch.field);
  const oldVal = formatValue(patch.oldValue);
  const newVal = formatValue(patch.value);

  // 항목 제목이 있으면 포함
  if (patch.itemTitle) {
    return `${patch.itemTitle} - ${fieldLabel}: ${oldVal} → ${newVal}`;
  }

  // 항목 제목이 없으면 필드명만
  return `${fieldLabel}: ${oldVal} → ${newVal}`;
}

/**
 * 변경 내용 요약 생성
 */
export function summarizePayload(payload: SyncPayload, type: EntityType): string {
  // 점검항목/데이터 패치가 있는 경우 → 실제 변경분만 표시
  if (payload.patches && payload.patches.length > 0) {
    const changed = payload.patches.filter((p) => formatValue(p.oldValue) !== formatValue(p.value));
    if (changed.length > 0) {
      return changed.map(formatPatchDetail).join("\n");
    }
  }

  // 설비정보 패치가 있는 경우
  if (type === "machine" && Array.isArray(payload.machineInfoPatches)) {
    const infoPatches = payload.machineInfoPatches as Array<{
      field: string;
      value?: string;
      oldValue?: string;
    }>;

    const changed = infoPatches.filter((p) => formatValue(p.oldValue) !== formatValue(p.value));
    if (changed.length > 0) {
      return changed
        .map((patch) => {
          const fieldLabel = toFieldLabel(patch.field);
          const oldVal = formatValue(patch.oldValue);
          const newVal = formatValue(patch.value);
          return `${fieldLabel}: ${oldVal} → ${newVal}`;
        })
        .join("\n");
    }
  }

  // 계측 타입
  if (type === "measure" && payload.measureType) {
    const key = payload.measureType as string;
    const measureLabel = toMeasureLabel(key);
    const indexLabel =
      payload.measureIndex != null ? ` #${(payload.measureIndex as number) + 1}` : "";
    if (payload.measureOldData) {
      const newData = payload.measureData as Record<string, unknown>;
      const oldData = payload.measureOldData as Record<string, unknown>;
      const fields = Object.keys(newData).filter(
        (f) =>
          f !== "createdAt" &&
          formatValue(oldData[f] as string | null | undefined) !==
            formatValue(newData[f] as string | null | undefined)
      );
      if (fields.length > 0) {
        const details = fields.map((f) => {
          const label = toFieldLabel(f);
          const oldVal = formatValue(oldData[f] as string | null | undefined);
          const newVal = formatValue(newData[f] as string | null | undefined);
          return `${label}: ${oldVal} → ${newVal}`;
        });
        return `${measureLabel}${indexLabel}\n${details.join("\n")}`;
      }
    }
    return `${measureLabel}${indexLabel}`;
  }

  // 설비 타입: machineKey → 한글 설비명
  if (type === "equipment" && payload.machineKey) {
    const key = payload.machineKey as string;
    return MACHINE_TITLES[key as MachineKey] ?? key;
  }

  // 사진 타입
  if (type === "photo") {
    const uploadType = (payload as { uploadType?: string }).uploadType;
    if (uploadType === "measurePhoto") {
      const slot = (payload as { slot?: string }).slot;
      const slotLabel = slot === "check" ? "점검사진" : slot === "result" ? "결과사진" : "사진";
      return `계측 ${slotLabel}`;
    }
    if (uploadType === "photoEtc") {
      return "사진대지 파일";
    }
    // caption이 있으면 표시, 없으면 기본 메시지
    return payload.caption ?? "사진 파일";
  }

  // 기타 타입
  return "";
}

/**
 * 타입 + 작업 유형 조합 라벨
 * 예: "점검항목 수정", "사진 추가"
 */
export function getActionLabel(
  type: EntityType,
  operation: SyncOperation,
  payload?: SyncPayload
): string {
  // photo 타입은 uploadType에 따라 구분
  if (type === "photo") {
    const uploadType = (payload as { uploadType?: string } | undefined)?.uploadType;
    if (uploadType === "measurePhoto") return "계측 사진 업로드";
    if (operation === "create") return "사진 대지 추가";
    if (operation === "delete") return "사진 대지 제거";
    if (operation === "update") return "사진 대지 변경";
  }

  const typeLabel = TYPE_LABELS[type] || type;
  const operationLabel = OPERATION_LABELS[operation] || operation;
  return `${typeLabel} ${operationLabel}`;
}
